import { useNavigate } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { cn } from '@/lib/utils';
import { AdvertiseLayout } from '@/components/advertise/AdvertiseLayout';
import { AdvertiseSideSummary } from '@/components/advertise/AdvertiseSideSummary';
import { AdvertiseFooterNav } from '@/components/advertise/AdvertiseFooterNav';
import { useAdvertise } from '@/store/useAdvertise';
import { useStepGuard, sellersStepValid } from '@/utils/advertiseGuards';
import { SELLERS_STEPS } from '@/config/advertiseSteps';

const ROLES = [
  { id: 'owner', label: 'I\u2019m the homeowner' },
  { id: 'listing-agent', label: 'I\u2019m the listing agent' },
  { id: 'co-agent', label: 'I\u2019m a co-agent' },
];

export default function SellersDetails() {
  const navigate = useNavigate();
  const sellers = useAdvertise((s) => s.sellers);
  const setField = useAdvertise((s) => s.setField);
  useStepGuard(sellersStepValid.details(sellers), '/advertise/sellers');

  const set = (patch) => setField('sellers', patch);
  const isAgentRole = sellers.role === 'listing-agent' || sellers.role === 'co-agent';
  const canProceed = !!sellers.role;

  return (
    <AdvertiseLayout
      trackLabel="Listing Boost"
      steps={SELLERS_STEPS}
      currentStepId="details"
      title="What's your role on this listing?"
      sidebar={<AdvertiseSideSummary items={[{ label: 'Role', value: ROLES.find((r) => r.id === sellers.role)?.label }]} />}
      footer={<AdvertiseFooterNav onBack={() => navigate('/advertise/sellers/verify')} onNext={() => navigate('/advertise/sellers/contact')} nextDisabled={!canProceed} />}
    >
      <RadioGroup value={sellers.role} onValueChange={(v) => set({ role: v })} className="gap-3">
        {ROLES.map((r) => (
          <Label
            key={r.id}
            htmlFor={`role-${r.id}`}
            className={cn('flex cursor-pointer items-center gap-3 rounded-2xl border p-4', sellers.role === r.id ? 'border-blue-600 bg-blue-50' : 'border-slate-200 hover:border-blue-300')}
          >
            <RadioGroupItem value={r.id} id={`role-${r.id}`} data-testid={`sellers-details-role-${r.id}`} />
            <span className="font-medium text-slate-900">{r.label}</span>
          </Label>
        ))}
      </RadioGroup>

      {isAgentRole && (
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <div><Label>MLS ID</Label><Input value={sellers.mlsId} onChange={(e) => set({ mlsId: e.target.value })} className="mt-1.5 rounded-xl" data-testid="sellers-details-mls-id" /></div>
          <div><Label>Brokerage</Label><Input value={sellers.brokerage} onChange={(e) => set({ brokerage: e.target.value })} className="mt-1.5 rounded-xl" data-testid="sellers-details-brokerage" /></div>
          <div className="sm:col-span-2">
            <Label>Is this listing already on the MLS?</Label>
            <RadioGroup value={sellers.onMls} onValueChange={(v) => set({ onMls: v })} className="mt-1.5 flex gap-4">
              <Label className="flex cursor-pointer items-center gap-2"><RadioGroupItem value="yes" id="on-mls-yes" />Yes</Label>
              <Label className="flex cursor-pointer items-center gap-2"><RadioGroupItem value="no" id="on-mls-no" />No</Label>
            </RadioGroup>
          </div>
        </div>
      )}
    </AdvertiseLayout>
  );
}
